// src/components/ui/PasswordConfirmModal.tsx
import { useEffect, useId, useRef, useState } from 'react';
import { brand, fonts } from '@/lib/tokens';
import { PasswordField } from '@/components/ui/PasswordField';

type Props = {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  submitting?: boolean;
  errorMessage?: string | null;
  onConfirm: (password: string) => void;
  onCancel: () => void;
};

export function PasswordConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  submitting = false,
  errorMessage,
  onConfirm,
  onCancel,
}: Props) {
  const [password, setPassword] = useState('');
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setPassword('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim() || submitting) return;
    onConfirm(password);
  }

  return (
    <div
      className="glass-overlay fixed inset-0 z-50 flex items-end justify-center px-6 pb-10"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <form
        className="glass-card glass-top-highlight slide-up-modal"
        style={{ width: '100%', maxWidth: 420, padding: 30 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 id={titleId} className="font-serif" style={{ fontSize: 22, lineHeight: 1.2, color: 'var(--text-primary)' }}>
          {title}
        </h2>
        {description && (
          <p className="mt-3 text-sm leading-relaxed" style={{ color: 'rgba(var(--fg-rgb),0.6)' }}>
            {description}
          </p>
        )}

        <div className="mt-5">
          <PasswordField
            inputRef={inputRef}
            label="Current password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            className="ghost-input mt-2"
          />
        </div>

        {errorMessage && (
          <p style={{ marginTop: 10, fontFamily: fonts.mono, fontSize: 11, color: brand.errorText }}>
            {errorMessage}
          </p>
        )}

        <div className="mt-7 flex items-center justify-end gap-2">
          <button type="button" className="noir-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="noir-button noir-button-primary"
            disabled={!password.trim() || submitting}
          >
            {submitting ? 'Confirming…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
